// frontend/src/pages/EditProductPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Loader2, Upload, X, Globe } from 'lucide-react';
import { SEOEditor } from '../components/SEO/SEOEditor';
import config from '../config';

const slugify = (text) => {
  if (!text) return '';

  let slug = text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')      // فاصله → خط تیره
    .replace(/_/g, '-')         // زیرخط → خط تیره
    .replace(/[^\w\u0600-\u06FF\-]/g, '')  // حذف کاراکترهای غیرمجاز
    .replace(/-+/g, '-')        // خط تیره تکراری → یک خط تیره
    .replace(/^-|-$/g, '');     // حذف خط تیره از ابتدا و انتها

  if (!slug) {
    slug = 'product-' + Date.now();
  }
  return slug;
};

export const EditProductPage = () => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [product, setProduct] = useState(null);
  const [showSEOPanel, setShowSEOPanel] = useState(false);
  const [manualSlug, setManualSlug] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    price: '',
    stock: '',
    status: 'active',
    warranty_months: 12,
    brand: '',
    power_rating: '',
    show_seller_name: true,
    show_price_on_call: false,
    contact_phone: '',
  });
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    focus_keyword: '',
  });

  // تابع بارگذاری داده‌ها
  const fetchData = async () => {
    setLoading(true);
    try {
      const catResponse = await fetch(config.getApiUrl('/products/categories/'));
      const catData = await catResponse.json();
      setCategories(Array.isArray(catData) ? catData : (catData.results || []));

      const accessToken = localStorage.getItem('access_token');
      // ✅ استفاده از config به جای localhost
      const productResponse = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!productResponse.ok) throw new Error('محصول یافت نشد');
      const productData = await productResponse.json();

      setFormData({
        name: productData.name,
        slug: productData.slug,
        category: productData.category?.id || '',
        description: productData.description || '',
        price: productData.price,
        stock: productData.stock,
        status: productData.status,
        warranty_months: productData.warranty_months,
        brand: productData.brand || '',
        power_rating: productData.power_rating || '',
        show_seller_name: productData.show_seller_name !== undefined ? productData.show_seller_name : true,
        show_price_on_call: productData.show_price_on_call || false,
        contact_phone: productData.contact_phone || '',
      });
      setManualSlug(true);
      setProduct(productData);
      setSeoData({
        meta_title: productData.meta_title || productData.name,
        meta_description: productData.meta_description || productData.description?.substring(0, 160) || '',
        meta_keywords: productData.meta_keywords || '',
        focus_keyword: productData.focus_keyword || '',
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('خطا در بارگذاری اطلاعات');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlSlug && urlSlug !== 'new') {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [urlSlug]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'name') {
      const newName = value;
      if (!manualSlug) {
        const generatedSlug = slugify(newName);
        setFormData(prev => ({ ...prev, name: newName, slug: generatedSlug }));
      } else {
        setFormData(prev => ({ ...prev, name: newName }));
      }
    } else if (name === 'slug') {
      setFormData(prev => ({ ...prev, slug: value }));
      setManualSlug(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSEOChange = (e) => {
    const { name, value } = e.target;
    setSeoData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSEO = async (newSeoData) => {
    const accessToken = localStorage.getItem('access_token');
    try {
      // ✅ استفاده از config
      const response = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          meta_title: newSeoData.meta_title,
          meta_description: newSeoData.meta_description,
          meta_keywords: newSeoData.meta_keywords,
          focus_keyword: newSeoData.focus_keyword,
        })
      });
      if (response.ok) {
        setSeoData(newSeoData);
        alert('تنظیمات سئو با موفقیت ذخیره شد');
        setShowSEOPanel(false);
      } else {
        alert('خطا در ذخیره تنظیمات سئو');
      }
    } catch (error) {
      console.error('Error saving SEO:', error);
      alert('خطا در ارتباط با سرور');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalSlug = formData.slug
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/_/g, '-')
      .replace(/[^\w\u0600-\u06FF\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    if (!finalSlug || finalSlug === '') {
      finalSlug = slugify(formData.name);
    }

    if (!finalSlug || finalSlug === '') {
      alert('آدرس یکتا (slug) نمی‌تواند خالی باشد');
      return;
    }
    // ✅ اعتبارسنجی قیمت: فقط اگر "تماس برای قیمت" فعال نباشد
    if (!formData.show_price_on_call) {
      if (!formData.price || parseInt(formData.price) <= 0) {
        alert('لطفاً قیمت را وارد کنید');
        return;
      }
    }
    const invalidChars = finalSlug.match(/[^\w\u0600-\u06FF\-]/g);
    if (invalidChars) {
      alert(`آدرس یکتا حاوی کاراکترهای غیرمجاز است: ${invalidChars.join(', ')}`);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    setSubmitting(true);

    try {
      const updateData = {
        name: formData.name,
        slug: finalSlug,
        category: parseInt(formData.category),
        description: formData.description,
        price: formData.show_price_on_call ? null : parseInt(formData.price),
        stock: parseInt(formData.stock),
        status: formData.status,
        warranty_months: parseInt(formData.warranty_months),
        brand: formData.brand || null,
        power_rating: formData.power_rating ? parseFloat(formData.power_rating) : null,
        show_seller_name: formData.show_seller_name,
        meta_title: seoData.meta_title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        focus_keyword: seoData.focus_keyword,
        show_price_on_call: formData.show_price_on_call,
        contact_phone: formData.contact_phone || null,
      };

      // ✅ استفاده از config
      const response = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedProduct = await response.json();
        const newSlug = updatedProduct.slug;
        alert('محصول با موفقیت ویرایش شد');
        if (newSlug !== urlSlug) {
          navigate(`/products/${encodeURIComponent(newSlug)}/edit`);
        } else {
          fetchData();
        }
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error updating product:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل تصویر انتخاب کنید');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم تصویر نباید بیشتر از 5 مگابایت باشد');
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const formDataImg = new FormData();
    formDataImg.append('image', file);
    formDataImg.append('product', product.id);
    if (product.images?.length === 0) formDataImg.append('is_main', 'true');
    else formDataImg.append('is_main', 'false');

    setUploadingImage(true);
    try {
      // ✅ استفاده از config
      const response = await fetch(config.getApiUrl('/products/images/'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formDataImg
      });
      if (response.ok) {
        alert('تصویر با موفقیت اضافه شد');
        const productResponse = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const newData = await productResponse.json();
        setProduct(newData);
      } else {
        alert('خطا در آپلود تصویر');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const deleteImage = async (imageId) => {
    const accessToken = localStorage.getItem('access_token');
    if (window.confirm('آیا از حذف این تصویر اطمینان دارید؟')) {
      try {
        // ✅ استفاده از config
        const response = await fetch(config.getApiUrl(`/products/images/${imageId}/`), {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        if (response.ok) {
          alert('تصویر با موفقیت حذف شد');
          const productResponse = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
            headers: { 'Authorization': `Bearer ${accessToken}` }
          });
          const newData = await productResponse.json();
          setProduct(newData);
        } else {
          alert('خطا در حذف تصویر');
        }
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('خطا در ارتباط با سرور');
      }
    }
  };

  const setAsMainImage = async (imageId) => {
    const accessToken = localStorage.getItem('access_token');
    try {
      // ✅ استفاده از config
      const response = await fetch(config.getApiUrl(`/products/images/${imageId}/`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ is_main: true })
      });
      if (response.ok) {
        alert('تصویر به عنوان اصلی تنظیم شد');
        const productResponse = await fetch(config.getApiUrl(`/products/products/${encodeURIComponent(urlSlug)}/`), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const newData = await productResponse.json();
        setProduct(newData);
      } else {
        alert('خطا در تنظیم تصویر اصلی');
      }
    } catch (error) {
      console.error('Error setting main image:', error);
      alert('خطا در ارتباط با سرور');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  const getKeywordSuggestions = () => {
    const words = formData.description?.split(/\s+/) || [];
    const wordCount = {};
    words.forEach(word => {
      if (word.length > 3) {
        wordCount[word] = (wordCount[word] || 0) + 1;
      }
    });
    return Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word]) => word);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">ویرایش محصول</h1>
            <button
              type="button"
              onClick={() => setShowSEOPanel(!showSEOPanel)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <Globe className="w-4 h-4" />
              {showSEOPanel ? 'بستن تنظیمات سئو' : 'تنظیمات سئو (پیشرفته)'}
            </button>
          </div>

          {showSEOPanel && (
            <SEOEditor
              initialData={seoData}
              onSave={handleSaveSEO}
              onCancel={() => setShowSEOPanel(false)}
              content={formData.description}
              title="تنظیمات سئوی محصول"
              keywordsSuggestion={getKeywordSuggestions()}
            />
          )}

          {/* بخش تصاویر */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">تصاویر محصول</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {product?.images?.map((img) => (
                <div key={img.id} className="relative group border rounded-lg overflow-hidden bg-white">
                  <img
                    src={img.image ? config.getMediaUrl(img.image) : '/placeholder-product.jpg'}
                    alt="Product"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button onClick={() => deleteImage(img.id)} className="bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {img.is_main && <span className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">اصلی</span>}
                  {!img.is_main && (
                    <button onClick={() => setAsMainImage(img.id)} className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                      اصلی کن
                    </button>
                  )}
                </div>
              ))}
            </div>
            <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition bg-white">
              <div className="text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm text-gray-500">{uploadingImage ? 'در حال آپلود...' : 'افزودن تصویر جدید'}</span>
              </div>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
            </label>
          </div>

          {/* فرم ویرایش */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نام محصول *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  آدرس یکتا (slug)
                  {manualSlug && <span className="text-xs text-blue-600 mr-2">(دستی)</span>}
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                  placeholder="مثال: محصول-جدید-123"
                />
                <small className="text-xs text-gray-500 block mt-1">
                  🔗 آدرس نهایی: {config.SITE_URL}/products/{formData.slug || '...'}
                </small>
                {!manualSlug && (
                  <small className="text-xs text-green-600 block mt-1">
                    💡 با تغییر نام محصول، این آدرس به‌طور خودکار ساخته می‌شود
                  </small>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی *</label>
                <select name="category" value={formData.category} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" required>
                  <option value="">انتخاب کنید</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2">
                  <option value="active">فعال</option>
                  <option value="inactive">غیرفعال</option>
                  <option value="out_of_stock">ناموجود</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">قیمت (تومان) *</label>
                <input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">موجودی *</label>
                <input type="number" name="stock" value={formData.stock} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">گارانتی (ماه)</label>
                <input type="number" name="warranty_months" value={formData.warranty_months} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">برند</label>
                <input type="text" name="brand" value={formData.brand} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" placeholder="مثال: JA Solar" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توان (کیلووات)</label>
                <input type="number" step="0.1" name="power_rating" value={formData.power_rating} onChange={handleInputChange} className="w-full border rounded-lg px-3 py-2" placeholder="مثال: 5.5" />
              </div>

              {/* ========== بخش تماس برای قیمت ========== */}
              <div className="col-span-1 md:col-span-2 border-t pt-4 mt-2">
                <h3 className="text-sm font-medium text-gray-700 mb-3">تنظیمات تماس برای قیمت</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="show_price_on_call"
                      checked={formData.show_price_on_call || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          show_price_on_call: checked,
                          price: checked ? '' : prev.price  // ✅ اگر فعال شد، قیمت را خالی کن
                        }));
                      }}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <label className="text-sm font-medium text-gray-700 cursor-pointer">
                        نمایش "تماس برای قیمت"
                      </label>
                      <p className="text-xs text-gray-500">در صورت فعال بودن، قیمت نمایش داده نمی‌شود و دکمه تماس نشان داده می‌شود</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">شماره تماس اختصاصی</label>
                    <input
                      type="text"
                      name="contact_phone"
                      value={formData.contact_phone || ''}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="مثال: 09123456789"
                      disabled={!formData.show_price_on_call}
                    />
                    <p className="text-xs text-gray-400 mt-1">در صورت خالی بودن، شماره اصلی سایت نمایش داده می‌شود</p>
                  </div>
                </div>
              </div>

              {/* ========== فیلد قیمت - شرطی ========== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  قیمت (تومان) {!formData.show_price_on_call && '*'}
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price || ''}
                  onChange={handleInputChange}
                  disabled={formData.show_price_on_call}  // ✅ در صورت فعال بودن "تماس برای قیمت"، غیرفعال شود
                  className={`w-full border rounded-lg px-3 py-2 ${formData.show_price_on_call ? 'bg-gray-100 text-gray-500' : ''
                    }`}
                  required={!formData.show_price_on_call}  // ✅ در صورت فعال بودن، الزامی نیست
                />
                {formData.show_price_on_call && (
                  <p className="text-xs text-orange-500 mt-1">
                    ⚠️ با فعال بودن "تماس برای قیمت"، قیمت محصول نمایش داده نمی‌شود
                  </p>
                )}
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" name="show_seller_name" checked={formData.show_seller_name} onChange={handleInputChange} className="w-4 h-4" />
                  <span className="text-sm text-gray-700">نمایش نام فروشنده در صفحه محصول</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات *</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows="6" className="w-full border rounded-lg px-3 py-2" required />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={() => navigate(`/products/${urlSlug}`)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                انصراف
              </button>
              <button type="submit" disabled={submitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
